/**
 * **Add a unit** — `docs/visit-page.md` §27.5, Board 18 H (popup 420): the
 * type filled, **Unit number** (required), **Lives at**, **Cost per hour**
 * (*internal · used for the visit's cost*), the green band *It goes into
 * Equipment's own list, so the module starts with it — nothing to enter
 * twice.* · **Cancel** · **Add MSS-03**.
 *
 * `POST /visits/:id/equipment-units` → `EquipmentPort.addUnit`: an `equipment`
 * row of that type (`added_from = 'visit'`), which the day cells then offer
 * as Board 5's unit picker does. The cost is internal: without *Costs &
 * margins* the field is absent and the unit takes the quote's rate (§2.3).
 */
import { useEffect, useState } from 'react'
import type { AddUnitOptions, EquipmentRead, EquipmentTypeRef, VisitRead } from '@rowted/shared'
import { PickField, SheetField, TextBox } from './equipment-fields.js'
import { useVisitTab, useVisitWrite } from './api.js'
import { ADD_UNIT } from './copy.js'
import { VisitGlyph } from './glyphs.js'
import { VisitButton, VisitPopup, VisitTile } from './sheet.js'

export function AddUnitPopup({ visitId, read, type, phone, onClose, onWritten }: {
  visitId: string
  read: VisitRead
  type: EquipmentTypeRef
  phone: boolean
  onClose: () => void
  onWritten: (next: EquipmentRead) => void
}) {
  const { data } = useVisitTab<AddUnitOptions>(visitId, '/equipment-units/options', { typeId: type.id })
  const write = useVisitWrite(visitId)
  const [unitNumber, setUnitNumber] = useState('')
  const [yardId, setYardId] = useState<string | null>(null)
  const [rate, setRate] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /* What the popup opens with: the visit's yard and the quote's rate for the type. */
  useEffect(() => {
    if (!data) return
    setYardId(data.yardId)
    setRate(data.internal?.rate ?? '')
  }, [data])

  const tag = unitNumber.trim()
  const ready = tag.length > 0 && !busy && (!data?.internal || rate.trim().length > 0)

  async function add() {
    if (!ready) return
    setBusy(true)
    setError(null)
    try {
      const result = await write('POST', '/equipment-units', {
        typeId: type.id,
        unitNumber: tag,
        yardId,
        ...(data?.internal ? { ratePerHour: rate.trim() } : {}),
      }, { tab: 'equipment' })
      if (result.tab) onWritten(result.tab as EquipmentRead)
      onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  const size = phone ? 'phone' : 'popup'
  return (
    <VisitPopup
      width={420}
      phone={phone}
      asDrawn="vp-18 H"
      title={ADD_UNIT.title}
      sub={ADD_UNIT.sub(type.noun, read.reference)}
      mark={<VisitTile glyph="loader" tone="indigo" size={34} />}
      onClose={onClose}
      footer={(
        <>
          <VisitButton size={size} onClick={onClose}>{ADD_UNIT.cancel}</VisitButton>
          <VisitButton size={size} tone="primary" disabled={!ready} onClick={() => void add()}>
            {tag ? ADD_UNIT.add(tag) : ADD_UNIT.title}
          </VisitButton>
        </>
      )}
    >
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-x-3.5 gap-y-3">
          <SheetField label={ADD_UNIT.type}>
            {({ id }) => <TextBox id={id} phone={phone} readOnly value={type.name} />}
          </SheetField>
          <SheetField label={ADD_UNIT.unitNumber} required>
            {({ id, describedBy }) => (
              <TextBox id={id} describedBy={describedBy} phone={phone} value={unitNumber} autoFocus
                onChange={setUnitNumber}
                onKeyDown={(event) => { if (event.key === 'Enter') void add() }} />
            )}
          </SheetField>
        </div>
        <div className="grid grid-cols-2 gap-x-3.5 gap-y-3">
          <SheetField label={ADD_UNIT.livesAt}>
            {({ id, describedBy }) => (
              <PickField id={id} describedBy={describedBy} label={ADD_UNIT.livesAt} value={yardId} phone={phone}
                chevron={false} onChange={setYardId}
                options={(data?.yards ?? []).map((yard) => ({ value: yard.id, label: yard.name }))} />
            )}
          </SheetField>
          {data?.internal && (
            <SheetField label={ADD_UNIT.costPerHour} hint={ADD_UNIT.costPerHourHelp}>
              {({ id, describedBy }) => (
                <TextBox id={id} describedBy={describedBy} phone={phone} inputMode="decimal" value={rate}
                  prefix={ADD_UNIT.dollar} suffix={ADD_UNIT.perHour} onChange={setRate} />
              )}
            </SheetField>
          )}
        </div>
        <div className="flex items-start gap-[9px] rounded-card border border-accent-line bg-accent-soft px-3 py-2.5 text-[12.5px] leading-[1.45] text-accent-dark">
          <VisitGlyph name="checkCircle" size={15} stroke={2} />
          <div>{ADD_UNIT.band}</div>
        </div>
        {error && <p className="m-0 text-[12.5px] text-danger">{error}</p>}
      </div>
    </VisitPopup>
  )
}
